import type {
  Asset,
  BonusType,
  LinkMode,
  Wheel,
  WheelPrizeType,
  WheelTheme,
} from '@longnecktech/splash-commons-fe';
import type {
  EnginePlayResponse,
  EngineWheel,
  EngineWheelConfig,
} from './engine.models';

export function mapEngineWheels(
  config: EngineWheelConfig,
  theme: WheelTheme,
  labels: Record<string, string>,
): EngineWheel[] {
  if (config.wheels === undefined)
    return [{ configId: null, game: mapEngineWheel(config, theme, labels) }];
  if (
    !config.wheels ||
    Array.isArray(config.wheels) ||
    !Object.keys(config.wheels).length
  )
    throw new Error('The game configuration has no wheels.');
  return Object.entries(config.wheels).map(([configId, segments]) => {
    if (!configId.trim())
      throw new Error('A wheel configuration ID is missing.');
    try {
      return {
        configId,
        game: mapEngineWheel({ ...config, segments }, theme, labels),
      };
    } catch (error) {
      throw new Error(
        `${configId}: ${error instanceof Error ? error.message : 'Invalid wheel configuration.'}`,
      );
    }
  });
}

export function mapEngineWheel(
  config: EngineWheelConfig,
  theme: WheelTheme,
  labels: Record<string, string>,
): Wheel {
  if (!config || !Array.isArray(config.segments) || !config.segments.length) {
    throw new Error('The game configuration has no wheel segments.');
  }
  const ids = new Set<string>();
  const indices = new Set<number>();
  const segments = config.segments
    .map((segment) => {
      if (
        !segment ||
        !segment.uuid ||
        (config.wheels === undefined && ids.has(segment.uuid))
      ) {
        throw new Error('The wheel segments need valid IDs.');
      }
      if (!Number.isSafeInteger(segment.index) || segment.index < 0) {
        throw new Error(
          'The wheel segment positions must be non-negative integers.',
        );
      }
      if (indices.has(segment.index)) {
        throw new Error(
          `Multiple wheel segments use position ${segment.index}.`,
        );
      }
      ids.add(segment.uuid);
      indices.add(segment.index);
      if (
        segment.reward &&
        Object.keys(segment.reward).length > 0 &&
        (!Number.isFinite(segment.reward.quantity) ||
          segment.reward.quantity! < 0 ||
          !segment.reward.displayName)
      ) {
        throw new Error('A wheel segment is missing its reward details.');
      }
      const icon = {
        uuid: segment.uuid,
        url: segment.iconAsset?.url ?? '',
        name: segment.reward?.displayName ?? segment.text,
        label: segment.reward?.displayName ?? segment.text,
      } as Asset;
      return {
        uuid: segment.uuid,
        index: segment.index,
        text: segment.text,
        iconAsset: icon,
        backgroundColor:
          segment.backgroundColor ??
          (segment.index % 2 ? '#ee0000' : '#00b3cd'),
        chance: 0,
        assetLabel: segment.reward?.displayName ?? segment.text,
        prize: {
          type: 'CUSTOM' as WheelPrizeType,
          currency: segment.reward?.unit ?? '',
          value: segment.reward?.quantity ?? 0,
          label: segment.reward?.displayName ?? '',
          infoLabel: '',
          image: icon,
          bonusType: (segment.reward?.type === 'FREE_SPINS'
            ? 'FREE_SPINS'
            : 'NON_DEPOSIT') as BonusType,
          slug: '',
        },
      };
    })
    .sort((a, b) => a.index - b.index);
  const translated = new Map(theme.labels.map((label) => [label.key, label]));
  for (const [key, value] of Object.entries(labels)) {
    if (typeof value === 'string')
      translated.set(key, { uuid: key, key, value, description: '', hint: '' });
  }
  return {
    uuid: config.uuid,
    name: config.name,
    startTs: config.startTs,
    endTs: config.endTs,
    paused: config.paused,
    ended: config.ended,
    theme: { ...theme, labels: [...translated.values()] },
    segments,
    hideCloseButton: false,
    termsLinkMode: 'HIDDEN' as LinkMode,
    gameRulesLinkMode: 'HIDDEN' as LinkMode,
    faqLinkMode: 'HIDDEN' as LinkMode,
    campaign: null,
    spinsPerDay: 0,
    delayBetweenSpins: 0,
    loginLink: null,
    featuredProducts: [],
    submissionCount: 0,
    termsAndConditions: null,
    rules: null,
    faq: null,
    settlementPostMessage: false,
    test: false,
  };
}

export function engineWinningSegment(
  game: Wheel,
  response: EnginePlayResponse,
  configId?: string | null,
) {
  const result = response.actions.filter((action) =>
    configId
      ? action.type === 'segment_result' && action.configId === configId
      : action.type === 'roll_result',
  );
  if (result.length !== 1) {
    throw new Error('The engine spin response has no unique landing position.');
  }
  const position = result[0].position;
  if (
    typeof position !== 'number' &&
    (typeof position !== 'string' || !/^\d+$/.test(position))
  ) {
    throw new Error('The engine returned an invalid landing position.');
  }
  const index = Number(position);
  const segment =
    Number.isSafeInteger(index) &&
    game.segments.find((item) => item.index === index);
  if (!segment)
    throw new Error(
      'The engine landing position does not match a wheel segment.',
    );
  if (
    !configId &&
    response.actions.some(
      (action) => action.type === 'move_forward' || action.type === 'move_back',
    )
  ) {
    throw new Error('The engine returned board movement for a wheel spin.');
  }
  return segment;
}

export function engineWinningSegments(
  wheels: EngineWheel[],
  response: EnginePlayResponse,
) {
  const results = response.actions.filter(
    (action) => action.type === 'segment_result',
  );
  if (wheels.length === 1 && wheels[0].configId === null && !results.length)
    return [
      { ...wheels[0], segment: engineWinningSegment(wheels[0].game, response) },
    ];
  if (results.length !== wheels.length)
    throw new Error(
      'The engine must return one landing position for each wheel.',
    );
  const matched = new Map<string, string>();
  for (const result of results) {
    if (typeof result.configId !== 'string' || !result.configId)
      throw new Error(
        'The engine returned a landing position without a wheel ID.',
      );
    const exact = wheels.find((wheel) => wheel.configId === result.configId);
    const wheel =
      exact ??
      wheels.find(
        (wheel) =>
          wheel.configId ===
          result.configId!.replace(
            /^wheel-segments-(\d+)$/,
            'wheel-segment-$1',
          ),
      );
    if (!wheel?.configId || matched.has(wheel.configId))
      throw new Error(
        'The engine returned an unknown or duplicate wheel result.',
      );
    matched.set(wheel.configId, result.configId);
  }
  return wheels.map((wheel) => ({
    ...wheel,
    segment: engineWinningSegment(
      wheel.game,
      response,
      matched.get(wheel.configId!),
    ),
  }));
}
